import {Component, Input, OnInit} from '@angular/core';
import {RealEstate, RealEstateCards} from '../../interfaces/real-estate';
import {AsyncPipe, CurrencyPipe, DatePipe, DecimalPipe, NgIf} from '@angular/common';
import {AuthenticationService} from '../../services/authentication/authentication.service';
import {Observable} from 'rxjs';
import {FavoritesService} from '../../services/favorites/favorites.service';
import {Router} from '@angular/router';
import {Base64Helper} from '../../utils/base64-helper';
import {FavoriteAlertConsentModalComponent} from '../favorite-alert-consent-modal/favorite-alert-consent-modal.component';
import {MarketingConsentService} from '../../services/marketing-consent/marketing-consent.service';

type RealEstateCard = RealEstateCards & Partial<RealEstate>;

@Component({
  selector: 'app-real-estate-card',
  imports: [
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    AsyncPipe,
    NgIf,
    FavoriteAlertConsentModalComponent
  ],
  templateUrl: './real-estate-card.component.html',
  styleUrl: './real-estate-card.component.css'
})
export class RealEstateCardComponent implements OnInit {
  private static hasShownFavoriteAlertConsentPreview = false;

  @Input() realEstate: RealEstateCard | null = null;

  isLogged: Observable<boolean>;
  favorites: Observable<string[]>;
  pendingFavoriteRealEstateId: string | null = null;
  showFavoriteAlertConsentModal = false;
  isSavingFavoriteAlertConsent = false;
  favoriteAlertConsentError = '';

  constructor(private authenticationService: AuthenticationService,
              private favoritesService: FavoritesService,
              private marketingConsentService: MarketingConsentService,
              private router: Router) {
    this.isLogged = this.authenticationService.isLogged;
    this.favorites = this.favoritesService.favoritesObservable;
  }

  ngOnInit() {
    // No need to subscribe to user$ and update isLogged manually
    // as we're now using the service's isLogged Observable
    this.showFavoriteAlertConsentPreview();
  }

  async includeFavorite(realEstateId: string): Promise<void> {
    try {
      const hasRecordedAlertDecision = sessionStorage.getItem('casato-real-estate-alerts-decision') === 'true'
        || await this.marketingConsentService.hasRecordedRealEstateAlertsDecision();

      if (hasRecordedAlertDecision) {
        this.favoritesService.includeFavorite(realEstateId);
        return;
      }
    } catch (error) {
      console.error('Failed to check favorite alert consent:', error);
    }

    this.openFavoriteAlertConsent(realEstateId);
  }

  private openFavoriteAlertConsent(realEstateId: string): void {
    this.pendingFavoriteRealEstateId = realEstateId;
    this.favoriteAlertConsentError = '';
    this.showFavoriteAlertConsentModal = true;
  }

  async saveFavoriteAlertConsent(receiveAlerts: boolean): Promise<void> {
    if (!this.pendingFavoriteRealEstateId) return;

    this.isSavingFavoriteAlertConsent = true;
    this.favoriteAlertConsentError = '';
    const realEstateId = this.pendingFavoriteRealEstateId;

    try {
      await this.marketingConsentService.saveRealEstateAlerts(receiveAlerts);
    } catch (error) {
      console.error('Failed to save favorite alert consent:', error);
    } finally {
      sessionStorage.setItem('casato-real-estate-alerts-decision', 'true');
      this.favoritesService.includeFavorite(realEstateId);
      this.closeFavoriteAlertConsent();
      this.isSavingFavoriteAlertConsent = false;
    }
  }

  private closeFavoriteAlertConsent(): void {
    this.showFavoriteAlertConsentModal = false;
    this.pendingFavoriteRealEstateId = null;
  }

  private showFavoriteAlertConsentPreview(): void {
    if (RealEstateCardComponent.hasShownFavoriteAlertConsentPreview) {
      return;
    }

    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('previewFavoriteAlertConsent') === 'true' && this.realEstate?.link) {
      RealEstateCardComponent.hasShownFavoriteAlertConsentPreview = true;
      this.openFavoriteAlertConsent(this.realEstate.link);
    }
  }

  deleteFavorite(realEstateId: string) {
    this.favoritesService.deleteFavorite(realEstateId);
  }

  navigateToDetails(id: string) {
    const encodedId = Base64Helper.encode(id);
    if (this.realEstate) {
      sessionStorage.setItem(`casato-real-estate-card-${encodedId}`, JSON.stringify(this.realEstate));
    }
    window.open('/propriedade/' + encodedId, '_blank');
  }

  // UI helpers for badges
  isRecentlyAdded(): boolean {
    if (!this.realEstate?.date_processed) return false;
    const processed = this.parseYMD(this.realEstate.date_processed);
    if (!processed) return false;
    const now = new Date();
    const diffMs = now.getTime() - processed.getTime();
    const fiveDaysMs = 5 * 24 * 60 * 60 * 1000;
    return diffMs >= 0 && diffMs <= fiveDaysMs;
  }

  isAuctionApproaching(): boolean {
    if (!this.realEstate) return false;
    const fields: (keyof RealEstate)[] = ['auction_1_start_date', 'auction_2_start_date', 'closing_bidding_date', 'deadline'];
    const now = new Date();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return fields.some((key) => {
      const value = this.realEstate?.[key] as unknown as string | undefined;
      if (!value) return false;
      const d = this.parseDate(value);
      if (!d) return false;
      const diff = d.getTime() - now.getTime();
      return diff >= 0 && diff <= sevenDaysMs;
    });
  }

  // Parses dates in format yyyy-mm-dd as UTC midnight
  private parseYMD(value: string): Date | null {
    if (!value) return null;
    const ymdRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = ymdRegex.exec(value);
    if (!match) return null;
    const [, y, mo, d] = match;
    const year = Number(y);
    const monthIndex = Number(mo) - 1;
    const day = Number(d);
    const utcMs = Date.UTC(year, monthIndex, day, 0, 0, 0, 0);
    const date = new Date(utcMs);
    return isNaN(date.getTime()) ? null : date;
  }

  // Parses dates strictly in UTC ISO format: %Y-%m-%dT%H:%M:%SZ (e.g., 2025-11-05T00:21:00Z)
  private parseDate(value: string | Date): Date | null {
    if (!value) return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    // Accept only strings like 2025-01-31T13:45:59Z
    const isoZRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})Z$/;
    const match = isoZRegex.exec(value);
    if (!match) return null;
    const [, y, mo, d, h, mi, s] = match;
    const year = Number(y);
    const monthIndex = Number(mo) - 1; // JS months are 0-based
    const day = Number(d);
    const hour = Number(h);
    const minute = Number(mi);
    const second = Number(s);
    const utcMs = Date.UTC(year, monthIndex, day, hour, minute, second, 0);
    const date = new Date(utcMs);
    return isNaN(date.getTime()) ? null : date;
  }

  protected readonly Array = Array;

  checkPastDate(date: string | undefined): boolean {
    if (!date) return false;

    // Expecting format: %Y-%m-%dT%H:%M:%SZ (e.g., 2025-12-18T07:32:00Z)
    const isoUtcPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
    if (!isoUtcPattern.test(date)) {
      console.warn('Invalid date format (expected %Y-%m-%dT%H:%M:%SZ):', date);
      return false;
    }

    const d = new Date(date);

    if (isNaN(d.getTime())) {
      console.warn('Unable to parse date:', date);
      return false;
    }

    const now = new Date();
    return now.getTime() > d.getTime();
  }

  checkAuctionPriceBelowAssessmentPrice(): boolean{
    const now = new Date();

    if (this.realEstate?.auction_1_end_date && this.realEstate?.auction_1_price){
      let auction_date = new Date(this.realEstate?.auction_1_end_date);
      if(now.getTime() > auction_date.getTime() && this.realEstate.assessment_price > this.realEstate.auction_1_price ){
        return true;
      }
    }

    if (this.realEstate?.auction_1_start_date && this.realEstate?.auction_2_start_date && this.realEstate?.auction_2_price){
      let auction_date = new Date(this.realEstate?.auction_1_start_date);
      // return true;
      if(now.getTime() > auction_date.getTime() && this.realEstate.assessment_price > this.realEstate.auction_2_price ){
        return true;
      }
    }

    return false;
  }

  // Decide which auction price should be highlighted based on current date
  // If the current date is after the start of the 1st auction and the 2nd auction exists,
  // show the 2nd auction price; otherwise show the 1st auction price when available.
  getCurrentAuctionPrice(): number | null {
    if (!this.realEstate) return null;

    const now = new Date();

    try {
      const auction1Start = this.realEstate.auction_1_start_date
        ? new Date(this.realEstate.auction_1_start_date as unknown as string)
        : null;

      // If we're already past the start of the first auction and there is a second auction price, prefer it
      if (auction1Start && now.getTime() > auction1Start.getTime() && this.realEstate.auction_2_price) {
        return this.realEstate.auction_2_price;
      }

      // Otherwise, prefer the first auction price if available
      if (this.realEstate.auction_1_price) {
        return this.realEstate.auction_1_price;
      }

      // Fallback to second auction price if first is not available
      if (this.realEstate.auction_2_price) {
        return this.realEstate.auction_2_price;
      }

      return null;
    } catch {
      // In case of any parsing issues, fallback gracefully
      return this.realEstate.auction_1_price ?? this.realEstate.auction_2_price ?? null;
    }
  }

  // Returns the discount percentage corresponding to the currently highlighted auction price
  // Mirrors the logic used in getCurrentAuctionPrice()
  getCurrentAuctionDiscount(): number | null {
    if (!this.realEstate) return null;

    const now = new Date();

    try {
      const auction1Start = this.realEstate.auction_1_start_date
        ? new Date(this.realEstate.auction_1_start_date as unknown as string)
        : null;

      // If past first auction start and second auction exists, use second discount when available
      if (auction1Start && now.getTime() > auction1Start.getTime() && this.realEstate.auction_2_price) {
        return this.realEstate.auction_2_discount ?? null;
      }

      // Otherwise, prefer first auction discount if first price is shown
      if (this.realEstate.auction_1_price) {
        return this.realEstate.auction_1_discount ?? null;
      }

      // Fallback: if showing second price due to absence of first price
      if (this.realEstate.auction_2_price) {
        return this.realEstate.auction_2_discount ?? null;
      }

      return null;
    } catch {
      // Fallback gracefully
      return this.realEstate.auction_1_discount ?? this.realEstate.auction_2_discount ?? null;
    }
  }

}
